import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, MessageCircle, Trophy, Activity, ArrowRight, ChevronUp } from "lucide-react";
import { api } from "@/lib/api";

function relTime(iso) {
    if (!iso) return "";
    const ms = Date.now() - new Date(iso).getTime();
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s/60)}m ago`;
    if (s < 86400) return `${Math.floor(s/3600)}h ago`;
    return `${Math.floor(s/86400)}d ago`;
}

function MiniAvatar({ url, name, size = 24 }) {
    const initial = (name || "?").charAt(0).toUpperCase();
    if (url) {
        const full = url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
        return <img src={full} alt="" className="rounded-full object-cover flex-shrink-0" style={{ width: size, height: size }} />;
    }
    return (
        <div className="rounded-full bg-[var(--ok-accent)] text-black font-display font-black flex items-center justify-center flex-shrink-0" style={{ width: size, height: size, fontSize: size * 0.45 }}>{initial}</div>
    );
}

function commentTargetHref(c) {
    if (c.target_type === "encounter") return `/encounters/${c.target_id}`;
    if (c.target_type === "media") return `/media-archive/${c.target_id}`;
    if (c.target_type === "species") return `/species/${c.target_id}`;
    return "/";
}

export function CommunityPulse() {
    const [activity, setActivity] = useState([]);
    const [trending, setTrending] = useState([]);
    const [leaders, setLeaders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [a, t, l] = await Promise.all([
                    api.get("/social/activity", { params: { limit: 6 } }),
                    api.get("/social/trending", { params: { limit: 5 } }),
                    api.get("/social/leaderboard", { params: { limit: 5 } }),
                ]);
                if (cancelled) return;
                setActivity(a.data.items || []);
                setTrending(t.data.items || []);
                setLeaders(l.data.items || []);
            } catch (_) { /* ignore */ }
            if (!cancelled) setLoading(false);
        })();
        return () => { cancelled = true; };
    }, []);

    const empty = !loading && activity.length === 0 && trending.length === 0 && leaders.length === 0;

    return (
        <section className="ok-container py-14" data-testid="community-pulse">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-50 mb-1.5">Section 03 · Living Archive</div>
                    <h2 className="font-display text-3xl lg:text-4xl">Community pulse</h2>
                    <p className="text-sm opacity-70 max-w-xl mt-2">Real researchers, real discussions, real-time investigation. This archive is a living network, not a museum.</p>
                </div>
                <Link to="/leaderboard" className="ok-btn-outline hidden md:inline-flex text-sm" data-testid="community-pulse-leaderboard">
                    Top Researchers <ArrowRight className="w-4 h-4" />
                </Link>
            </div>

            {empty ? (
                <div className="ok-card p-8 text-center">
                    <Activity className="w-8 h-8 mx-auto opacity-30 mb-3" />
                    <h3 className="font-display text-xl mb-2">The investigation begins now</h3>
                    <p className="opacity-70 text-sm max-w-md mx-auto mb-5">No discussions yet. Be the first to weigh in on any case. Your reputation grows with every upvoted insight.</p>
                    <div className="flex gap-3 justify-center">
                        <Link to="/encounters?source_type=historical" className="ok-btn-primary text-sm" data-testid="pulse-cta-cases">
                            Browse cases <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link to="/register" className="ok-btn-outline text-sm" data-testid="pulse-cta-register">
                            Create account
                        </Link>
                    </div>
                </div>
            ) : (
                <div className="grid lg:grid-cols-12 gap-5">
                    {/* TRENDING (8 cols) */}
                    <div className="lg:col-span-8 space-y-5 min-w-0">
                        <div className="ok-card p-5">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2 text-sm font-display">
                                    <Flame className="w-4 h-4 text-[var(--ok-accent)]" />
                                    Trending investigations
                                </div>
                                <Link to="/encounters?source_type=historical" className="text-xs font-mono opacity-60 hover:text-[var(--ok-accent)]">
                                    All cases →
                                </Link>
                            </div>
                            {trending.length === 0 ? (
                                <div className="text-xs opacity-50 py-3">No active discussions yet. Be the first.</div>
                            ) : (
                                <div className="divide-y" style={{ borderColor: "var(--ok-border)" }}>
                                    {trending.map((e, i) => (
                                        <Link key={e.id} to={`/encounters/${e.id}`} className="flex items-center gap-3 py-3 hover:bg-[var(--ok-surface-hover)] -mx-2 px-2 rounded transition" data-testid={`pulse-trending-${e.id}`}>
                                            <div className="font-display text-xl opacity-30 w-6 text-center">{i + 1}</div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium truncate">{e.title}</div>
                                                <div className="text-[10px] font-mono uppercase tracking-wide opacity-50 truncate">{e.location || "—"} · {(e.encounter_date || "").slice(0, 4)}</div>
                                            </div>
                                            <div className="flex items-center gap-2 text-xs font-mono opacity-70 flex-shrink-0">
                                                <MessageCircle className="w-3 h-3" />
                                                <span>{e.comment_count || 0}</span>
                                                {(e.vote_score || 0) > 0 && (
                                                    <>
                                                        <ChevronUp className="w-3 h-3 text-[var(--ok-accent)] ml-1" />
                                                        <span>{e.vote_score}</span>
                                                    </>
                                                )}
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="ok-card p-5">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2 text-sm font-display">
                                    <Activity className="w-4 h-4 text-[var(--ok-accent)]" />
                                    Most active discussions
                                </div>
                            </div>
                            {activity.length === 0 ? (
                                <div className="text-xs opacity-50 py-3">No recent activity yet.</div>
                            ) : (
                                <div className="space-y-3">
                                    {activity.slice(0, 5).map((c) => (
                                        <Link key={c.id} to={commentTargetHref(c)} className="flex items-start gap-3 -mx-2 px-2 py-2 hover:bg-[var(--ok-surface-hover)] rounded transition" data-testid={`pulse-activity-${c.id}`}>
                                            <MiniAvatar url={c.user_avatar_url} name={c.user_name} size={28} />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs font-mono opacity-60 flex items-center gap-2 truncate">
                                                    <span className="text-[var(--ok-accent)] truncate">{c.user_name || "Witness"}</span>
                                                    <span>commented on</span>
                                                    <span className="text-white/80 truncate">{c.target_title}</span>
                                                    <span className="ml-auto opacity-60 flex-shrink-0">{relTime(c.created_at)}</span>
                                                </div>
                                                <p className="text-sm opacity-85 line-clamp-2 mt-0.5">{c.body}</p>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* TOP RESEARCHERS (4 cols) */}
                    <div className="lg:col-span-4 min-w-0">
                        <div className="ok-card p-5 h-full">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2 text-sm font-display">
                                    <Trophy className="w-4 h-4 text-[var(--ok-accent)]" />
                                    Top researchers
                                </div>
                                <Link to="/leaderboard" className="text-xs font-mono opacity-60 hover:text-[var(--ok-accent)]">
                                    Full board →
                                </Link>
                            </div>
                            {leaders.length === 0 ? (
                                <div className="text-xs opacity-50 py-3">No rankings yet. First contributors lead the board.</div>
                            ) : (
                                <div className="space-y-2.5">
                                    {leaders.map((u, i) => (
                                        <Link key={u.id} to={`/u/${u.id}`} className="flex items-center gap-3 hover:bg-[var(--ok-surface-hover)] -mx-2 px-2 py-1.5 rounded transition" data-testid={`pulse-leader-${u.id}`}>
                                            <div className={`font-display text-lg w-5 text-center ${i < 3 ? "text-[var(--ok-accent)]" : "opacity-40"}`}>{i + 1}</div>
                                            <MiniAvatar url={u.avatar_url} name={u.name} size={32} />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium truncate">{u.name}</div>
                                                {u.founding_member ? <div className="text-[10px] uppercase tracking-wide font-mono opacity-60">Founding</div> : null}
                                            </div>
                                            <div className="font-display text-base text-[var(--ok-accent)] tabular-nums">{u.reputation}</div>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
